import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Crown, CreditCard, KeyRound, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "h-10 rounded-none bg-[#0A0A0A] border-white/10 focus-visible:ring-[#004AAD]";
const STATUS_LABEL = { active: "Active", trialing: "Trial", past_due: "Payment due", canceled: "Cancelled", none: "No plan" };

export default function SubscriptionDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setShowPw(false); setCur(""); setNext("");
    api.get("/subscription").then(({ data }) => setSub(data)).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  const savePw = async () => {
    if (!cur || !next) { toast.error("Fill in both password fields"); return; }
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { currentPassword: cur, newPassword: next });
      toast.success(data.message || "Password updated");
      setShowPw(false); setCur(""); setNext("");
    } catch (e) { toast.error(apiError(e, "Could not update password")); }
    finally { setSaving(false); }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e, "Could not open billing portal")); setPortalLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="subscription-dialog" className="bg-[#0A0A0A] border-white/10 rounded-none text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight text-white">Subscription</DialogTitle>
          <DialogDescription className="text-sm text-zinc-200 tracking-wide">Manage your plan, billing and password.</DialogDescription>
        </DialogHeader>

        {loading || !sub ? (
          <div className="flex items-center gap-2 text-zinc-200 py-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-5">
            <div className="border border-[#E2E30A]/40 bg-[#E2E30A]/5 p-4" data-testid="subscription-current">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Crown className="h-4 w-4 text-[#E2E30A]" />
                  <span className="font-display text-xl uppercase text-white">Performance Nutrition</span>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#34C759]/50 text-[#34C759]" data-testid="subscription-status">{STATUS_LABEL[sub.status] || sub.status}</span>
                </div>
                <span className="text-sm text-zinc-200">{sub.priceLabel}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                <div><p className="text-[10px] uppercase tracking-wider text-zinc-200">Member</p><p className="text-white break-all">{sub.name || sub.email}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-zinc-200">Email</p><p className="text-white break-all">{sub.email}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-zinc-200">Member since</p><p className="text-white">{fmt(sub.memberSince)}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-zinc-200">Renews / expires</p><p className="text-white">{fmt(sub.currentPeriodEnd || sub.compUntil)}</p></div>
              </div>
              {sub.cancelAtPeriodEnd && <p className="text-xs text-[#FF9500] mt-3">Your plan will not renew — access ends on {fmt(sub.currentPeriodEnd)}.</p>}
            </div>

            {/* Billing actions */}
            {!sub.hasAccess ? (
              <Button data-testid="subscribe-cta-btn" onClick={() => { onOpenChange(false); navigate("/subscribe"); }}
                className="w-full h-11 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold uppercase tracking-wide transition-colors">
                <Sparkles className="h-4 w-4 mr-2" /> Start 7-day free trial
              </Button>
            ) : sub.hasStripe ? (
              <Button data-testid="manage-billing-btn" onClick={openPortal} disabled={portalLoading}
                className="w-full h-11 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold uppercase tracking-wide transition-colors">
                {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-2" /> Manage billing & cancel</>}
              </Button>
            ) : (
              <div className="border border-white/10 p-4 text-sm text-zinc-200">Your access is active. No billing account is attached to this login.</div>
            )}

            {/* Change password */}
            <div className="border border-white/10 p-4">
              {!showPw ? (
                <Button data-testid="open-change-password" variant="outline" onClick={() => setShowPw(true)} className="w-full h-10 rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white uppercase text-xs tracking-wider">
                  <KeyRound className="h-3.5 w-3.5 mr-2" /> Change password
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs uppercase tracking-[0.15em] text-zinc-200">Change password</p>
                  <Input data-testid="current-password-input" type="password" className={inputCls} placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} />
                  <Input data-testid="new-password-input" type="password" className={inputCls} placeholder="New password (min 8 chars)" value={next} onChange={(e) => setNext(e.target.value)} />
                  <div className="flex gap-2">
                    <Button data-testid="save-password-btn" onClick={savePw} disabled={saving} className="flex-1 h-10 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold uppercase text-xs tracking-wider transition-colors">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                    </Button>
                    <Button variant="ghost" onClick={() => setShowPw(false)} className="h-10 rounded-none text-zinc-200 hover:bg-white/5 uppercase text-xs tracking-wider">Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
